// Client Executive Summary — page shell.
//
// Three tabs:
//   1. Build Your Own    — the wizard (upload → generate → download)
//   2. Library           — repository of all past summaries + edit action
//   3. Build v1 Preview  — the locked sample deck + feedback
//
// The Library tab hands an edit target down to the Wizard via editSummaryId;
// the Wizard rehydrates from the saved payload_json and switches to its
// Review step so the user can adjust imagery/narrative/rooms and regenerate.

import React, { useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import BuildV1Preview from './BuildV1Preview';
import SummaryLibrary from './SummaryLibrary';
import Wizard from './Wizard';

const NAVY = '#1A5490';

export default function ClientExecutiveSummary() {
  const [tab, setTab] = useState(0);
  // When the user clicks Edit on a row in the Library, we stash the id here
  // and bounce them to the Wizard tab. Wizard reads editSummaryId and loads
  // the payload on mount / when the id changes.
  const [editSummaryId, setEditSummaryId] = useState(null);
  // Bump this to force the Wizard to reload the same id if the user clicks
  // Edit again after abandoning a previous edit session.
  const [editNonce, setEditNonce] = useState(0);

  const handleEdit = (id) => {
    setEditSummaryId(id);
    setEditNonce((n) => n + 1);
    setTab(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ color: NAVY, fontWeight: 300, mb: 0.5 }}>
        Client Executive Summary
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Upload your D-Tools proposals, and generate a client-facing executive summary
        that leads with the total investment and holds a consistent ACS brand.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          borderBottom: 1, borderColor: 'divider', mb: 3,
          '& .MuiTab-root': { textTransform: 'uppercase', letterSpacing: '0.14em', fontSize: 12, fontWeight: 500 },
          '& .Mui-selected': { color: NAVY },
          '& .MuiTabs-indicator': { backgroundColor: NAVY, height: 3 },
        }}
      >
        <Tab label="Build Your Own" />
        <Tab label="Library" />
        <Tab label="Build v1 Preview" />
      </Tabs>

      {tab === 0 && (
        <Wizard
          editSummaryId={editSummaryId}
          editNonce={editNonce}
          onEditConsumed={() => setEditSummaryId(null)}
        />
      )}
      {tab === 1 && <SummaryLibrary onEdit={handleEdit} />}
      {tab === 2 && <BuildV1Preview />}
    </Box>
  );
}
